import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import AuthNavbar from "./AuthNavbar";
import LoadingState from "../Common/LoadingState";
import { verifyEmailToken, getVerificationStatus, resendEmailVerification } from "../../services/auth";
import "./Auth.css";

interface EmailVerificationProps {
    email?: string;
    onVerificationSuccess?: () => void;
}

enum VerificationState {
    CHECKING,
    VERIFIED,
    PENDING,
    ERROR,
    RESENDING,
    REDIRECTING,
}

const EmailVerification: React.FC<EmailVerificationProps> = ({
    email: propEmail,
    onVerificationSuccess
}) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // State management
    const [state, setState] = useState<VerificationState>(VerificationState.CHECKING);
    const [email, setEmail] = useState<string>(propEmail || "");
    const [message, setMessage] = useState<string>("");
    const [error, setError] = useState<string | null>(null);
    const [countdown, setCountdown] = useState<number>(5);
    const [verificationStatus, setVerificationStatus] = useState<{
        email_verified: boolean;
        phone_verified: boolean;
        can_access_dashboard: boolean;
    } | null>(null);

    // Simple processing flags using refs
    const processedRef = useRef<boolean>(false);

    // Handle token-based verification (from email link)
    const handleTokenVerification = useCallback(async (token: string) => {
        if (processedRef.current) {
            console.log("Token already processed, skipping");
            return;
        }

        try {
            setState(VerificationState.CHECKING);
            setError(null);
            setMessage("Verifying your email address...");
            processedRef.current = true;

            const result = await verifyEmailToken(token);

            if (result.success) {
                setState(VerificationState.VERIFIED);
                
                // Check if email was already verified
                if (result.data?.already_verified) {
                    setMessage("Your email is already verified!");
                } else {
                    setMessage("Email verified successfully!");
                }

                // Extract email from response if available
                if (result.data?.email) {
                    setEmail(result.data.email);
                    localStorage.setItem("email", result.data.email);
                }

                // Set verification status
                setVerificationStatus({
                    email_verified: true,
                    phone_verified: result.data?.phone_verified || false,
                    can_access_dashboard: result.data?.phone_verified || false
                });

                // Call success callback
                if (onVerificationSuccess) {
                    onVerificationSuccess();
                }

                // Determine next step
                if (!result.data?.phone_verified) {
                    setMessage("Email verified successfully! Redirecting you to phone verification...");
                    setState(VerificationState.REDIRECTING);
                    
                    let timeLeft = 3;
                    setCountdown(timeLeft);
                    
                    const countdownInterval = setInterval(() => {
                        timeLeft -= 1;
                        setCountdown(timeLeft);
                        
                        if (timeLeft <= 0) {
                            clearInterval(countdownInterval);
                            navigate('/phone-verification');
                        }
                    }, 1000);
                } else {
                    setMessage("Email verified successfully! Redirecting you to dashboard...");
                    setState(VerificationState.REDIRECTING);
                    
                    let timeLeft = 3;
                    setCountdown(timeLeft);
                    
                    const countdownInterval = setInterval(() => {
                        timeLeft -= 1;
                        setCountdown(timeLeft);
                        
                        if (timeLeft <= 0) {
                            clearInterval(countdownInterval);
                            navigate('/dashboard');
                        }
                    }, 1000);
                }
            } else {
                setState(VerificationState.ERROR);
                setError(result.error || "Failed to verify email. The link may be expired or invalid.");
            }
        } catch (err) {
            setState(VerificationState.ERROR);
            setError("An unexpected error occurred during verification. Please try again.");
            console.error("Token verification error:", err);
        }
    }, [navigate, onVerificationSuccess]);

    // Handle initial status check for users without tokens
    const handleInitialStatusCheck = useCallback(async () => {
        if (processedRef.current) {
            return;
        }

        try {
            setState(VerificationState.CHECKING);
            setError(null);
            setMessage("Checking verification status...");

            const storedEmail = localStorage.getItem("email");
            if (!storedEmail) {
                setState(VerificationState.ERROR);
                setError("No email found. Please log in again.");
                return;
            }

            setEmail(storedEmail);
            processedRef.current = true;

            const statusResult = await getVerificationStatus(storedEmail);
            
            if (statusResult.success && statusResult.data) {
                const { email_verified, phone_verified, can_access_dashboard } = statusResult.data;
                
                setVerificationStatus(statusResult.data);

                if (email_verified && phone_verified && can_access_dashboard) {
                    setState(VerificationState.VERIFIED);
                    setMessage("You are already verified! Redirecting to dashboard...");
                    setTimeout(() => navigate('/dashboard'), 2000);
                } else if (email_verified && !phone_verified) {
                    setState(VerificationState.VERIFIED);
                    setMessage("Email already verified! Redirecting to phone verification...");
                    setTimeout(() => navigate('/phone-verification'), 2000);
                } else {
                    setState(VerificationState.PENDING);
                    setMessage("Please check your email for the verification link.");
                }
            } else {
                setState(VerificationState.PENDING);
                setMessage("Please check your email for the verification link.");
            }
        } catch (error) {
            console.error("Error checking initial verification status:", error);
            setState(VerificationState.PENDING);
            setMessage("Please check your email for the verification link.");
        }
    }, [navigate]);

    // Main effect - handle token verification or initial status check
    useEffect(() => {
        // Prevent multiple executions
        if (processedRef.current) {
            return;
        }

        const token = searchParams.get('token');
        const emailParam = searchParams.get('email');

        // Set email if provided in URL
        if (emailParam) {
            setEmail(emailParam);
        }

        // Process token or check status
        if (token) {
            console.log("Processing email verification token");
            handleTokenVerification(token);
        } else {
            console.log("No token found, checking initial status");
            handleInitialStatusCheck();
        }
    }, []); // Only run once on mount

    // Resend verification email
    const handleResendVerification = async () => {
        if (!email) {
            setError("Email address is required to resend verification.");
            return;
        }

        try {
            setState(VerificationState.RESENDING);
            setError(null);

            const result = await resendEmailVerification(email);

            if (result.success) {
                setState(VerificationState.PENDING);
                setMessage("Verification email sent! Please check your inbox and spam folder.");
            } else {
                setState(VerificationState.PENDING);
                setError(result.error || "Failed to resend verification email. Please try again.");
            }
        } catch (err) {
            setState(VerificationState.PENDING);
            setError("Failed to resend verification email. Please try again.");
            console.error("Resend error:", err);
        }
    };

    // Render loading state
    if (state === VerificationState.CHECKING) {
        return (
            <>
                <AuthNavbar />
                <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
                    <motion.div
                        className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                        <div className="flex flex-col items-center">
                            <motion.div
                                className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="32"
                                    height="32"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="text-blue-600"
                                >
                                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
                                </svg>
                            </motion.div>
                            <h2 className="text-xl font-bold text-[color:var(--foreground)] text-center mb-2">
                                Verifying Your Email
                            </h2>
                            <p className="text-[color:var(--muted-foreground)] text-center">
                                {message || "Please wait while we verify your email address..."}
                            </p>
                        </div>
                    </motion.div>
                </div>
            </>
        );
    }

    // Render redirecting state
    if (state === VerificationState.REDIRECTING) {
        return (
            <>
                <AuthNavbar />
                <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
                    <motion.div
                        className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                        <div className="flex flex-col items-center mb-6">
                            <motion.div
                                className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <motion.svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="32"
                                    height="32"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="text-green-600"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 0.6, delay: 0.4 }}
                                >
                                    <path d="M20 6L9 17l-5-5"></path>
                                </motion.svg>
                            </motion.div>

                            <motion.h2
                                className="text-2xl font-bold text-[color:var(--foreground)] text-center"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                {message.includes("already verified") ? "Already Verified!" : "Email Verified!"}
                            </motion.h2>

                            <motion.p
                                className="text-sm text-[color:var(--muted-foreground)] mt-2 text-center"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                            >
                                {message}
                            </motion.p>
                        </div>

                        <motion.div
                            className="space-y-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                        >
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="flex items-center justify-center">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                        <span className="text-blue-600 font-bold text-lg">{countdown}</span>
                                    </div>
                                    <p className="text-sm text-blue-800">
                                        {verificationStatus?.phone_verified === false ? 
                                            "Redirecting to phone verification..." : 
                                            "Redirecting to dashboard..."
                                        }
                                    </p>
                                </div>
                            </div>

                            <div className="flex space-x-3">
                                {verificationStatus?.phone_verified === false && (
                                    <motion.button
                                        onClick={() => navigate('/phone-verification')}
                                        className="flex-1 py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] transition-colors"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        Continue Now
                                    </motion.button>
                                )}

                                {verificationStatus?.can_access_dashboard && (
                                    <motion.button
                                        onClick={() => navigate('/dashboard')}
                                        className="flex-1 py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] transition-colors"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        Go to Dashboard
                                    </motion.button>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </>
        );
    }

    // Render success state
    if (state === VerificationState.VERIFIED) {
        return (
            <>
                <AuthNavbar />
                <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
                    <motion.div
                        className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                        <div className="flex flex-col items-center mb-6">
                            <motion.div
                                className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <motion.svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="32"
                                    height="32"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="text-green-600"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 0.6, delay: 0.4 }}
                                >
                                    <path d="M20 6L9 17l-5-5"></path>
                                </motion.svg>
                            </motion.div>

                            <motion.h2
                                className="text-2xl font-bold text-[color:var(--foreground)] text-center"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                Email Verified!
                            </motion.h2>

                            <motion.p
                                className="text-sm text-[color:var(--muted-foreground)] mt-2 text-center"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                            >
                                {message}
                            </motion.p>
                        </div>

                        <motion.div
                            className="space-y-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                        >
                            {verificationStatus && !verificationStatus.phone_verified && (
                                <motion.div
                                    className="p-4 bg-blue-50 border border-blue-200 rounded-md"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.6 }}
                                >
                                    <p className="text-sm text-blue-800">
                                        <strong>Next step:</strong> Verify your phone number to complete registration and access your dashboard.
                                    </p>
                                </motion.div>
                            )}

                            <div className="flex space-x-3">
                                {verificationStatus?.phone_verified === false && (
                                    <motion.button
                                        onClick={() => navigate('/phone-verification')}
                                        className="flex-1 py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] transition-colors"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: 0.7 }}
                                    >
                                        Verify Phone Number
                                    </motion.button>
                                )}

                                {verificationStatus?.can_access_dashboard && (
                                    <motion.button
                                        onClick={() => navigate('/dashboard')}
                                        className="flex-1 py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] transition-colors"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: 0.7 }}
                                    >
                                        Go to Dashboard
                                    </motion.button>
                                )}
                            </div>

                            <motion.div
                                className="text-center"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.8 }}
                            >
                                <Link
                                    to="/login"
                                    className="text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium"
                                >
                                    Continue to Login
                                </Link>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </div>
            </>
        );
    }

    // Render pending verification state
    if (state === VerificationState.PENDING || state === VerificationState.RESENDING) {
        return (
            <>
                <AuthNavbar />
                <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
                    <motion.div
                        className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                    >
                        <div className="flex flex-col items-center mb-6">
                            <motion.div
                                className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4"
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                {state === VerificationState.RESENDING ? (
                                    <motion.svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="32"
                                        height="32"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="text-blue-600"
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                    >
                                        <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                                    </motion.svg>
                                ) : (
                                    <motion.svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="32"
                                        height="32"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="text-blue-600"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.6, delay: 0.4 }}
                                    >
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                        <polyline points="22,6 12,13 2,6"></polyline>
                                    </motion.svg>
                                )}
                            </motion.div>

                            <motion.h2
                                className="text-2xl font-bold text-[color:var(--foreground)] text-center"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.3 }}
                            >
                                {state === VerificationState.RESENDING ? "Sending Email..." : "Check Your Email"}
                            </motion.h2>

                            <motion.p
                                className="text-sm text-[color:var(--muted-foreground)] mt-2 text-center"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                            >
                                {state === VerificationState.RESENDING 
                                    ? "We're sending a new verification email to your inbox..."
                                    : email 
                                        ? `We've sent a verification link to ${email}` 
                                        : "Please check your email for the verification link"
                                }
                            </motion.p>
                        </div>

                        <motion.div
                            className="space-y-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                        >
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                                <p className="text-sm text-blue-800 mb-2">
                                    <strong>Verification Steps:</strong>
                                </p>
                                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                                    <li>Check your email inbox (including spam/junk folder)</li>
                                    <li>Click the verification link in the email</li>
                                    <li>You'll be redirected to verify your phone number</li>
                                    <li>Complete phone verification to access your account</li>
                                </ol>
                            </div>

                            {error && (
                                <motion.div
                                    className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {error}
                                </motion.div>
                            )}

                            {message && !error && (
                                <motion.div
                                    className="p-3 bg-green-50 border border-green-200 rounded-md text-sm"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="flex items-center">
                                        <div className="shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="text-green-600"
                                            >
                                                <path d="M20 6L9 17l-5-5"></path>
                                            </svg>
                                        </div>
                                        <span className="text-green-600 font-medium">{message}</span>
                                    </div>
                                </motion.div>
                            )}

                            <motion.button
                                onClick={handleResendVerification}
                                disabled={state === VerificationState.RESENDING}
                                className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.6 }}
                            >
                                {state === VerificationState.RESENDING ? "Sending..." : "Resend Verification Email"}
                            </motion.button>

                            <motion.div
                                className="text-center"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.7 }}
                            >
                                <Link
                                    to="/signup"
                                    className="text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium"
                                >
                                    ← Back to signup
                                </Link>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </div>
            </>
        );
    }

    // Render error state
    return (
        <>
            <AuthNavbar />
            <div className="min-h-screen flex items-center justify-center bg-[color:var(--background)] py-12 px-4 sm:px-6 lg:px-8 relative pt-20">
                <motion.div
                    className="max-w-md w-full bg-[color:var(--card)] rounded-lg shadow-xl p-8"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <div className="flex flex-col items-center mb-6">
                        <motion.div
                            className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <motion.svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="text-red-600"
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                            >
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                            </motion.svg>
                        </motion.div>

                        <motion.h2
                            className="text-2xl font-bold text-[color:var(--foreground)] text-center"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            Verification Issue
                        </motion.h2>

                        <motion.p
                            className="text-sm text-[color:var(--muted-foreground)] mt-2 text-center"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                        >
                            {error || "We encountered an issue verifying your email address"}
                        </motion.p>
                    </div>

                    <motion.div
                        className="space-y-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                    >
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                            <p className="text-sm text-amber-800 mb-2">
                                <strong>What you can try:</strong>
                            </p>
                            <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                                <li>Check if the verification link has expired</li>
                                <li>Request a new verification email</li>
                                <li>Check your spam/junk folder for emails</li>
                                <li>Make sure you're using the latest link</li>
                            </ul>
                        </div>

                        <div className="space-y-3">
                            {email && (
                                <motion.button
                                    onClick={handleResendVerification}
                                    disabled={false}
                                    className="w-full py-2 px-4 bg-[color:var(--color-secondary)] text-white rounded-md text-sm font-medium hover:bg-[color:var(--color-secondary)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[color:var(--color-secondary)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.6 }}
                                >
                                    Send New Verification Email
                                </motion.button>
                            )}

                            <motion.div
                                className="grid grid-cols-2 gap-3"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.7 }}
                            >
                                <Link
                                    to="/signup"
                                    className="text-center py-2 px-3 text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium border border-gray-300 rounded-md hover:border-[color:var(--color-secondary)] transition-colors"
                                >
                                    ← Back to Signup
                                </Link>
                                <Link
                                    to="/login"
                                    className="text-center py-2 px-3 text-sm text-[color:var(--muted-foreground)] hover:text-[color:var(--color-secondary)] font-medium border border-gray-300 rounded-md hover:border-[color:var(--color-secondary)] transition-colors"
                                >
                                    Go to Login →
                                </Link>
                            </motion.div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </>
    );
};

export default EmailVerification;